/**
 * WebSocket-Client fürs UI (Schritt 5). Verbindet sich mit dem Worker-`/ws`,
 * abonniert pro aktivem Server das Topic `server:<id>` und reicht eingehende
 * Live-Events (chat/status/players/flags/fbf) an `onEvent`. Reconnect mit
 * Backoff; offene Abos werden nach einem Reconnect erneut gesendet. Kein
 * Polling. Die WebSocket-Implementierung ist injizierbar (Test-Mock).
 */

export interface WsEvent {
  type: "chat" | "status" | "players" | "flags" | "fbf" | string;
  server: string;
  ts: number;
  data: unknown;
}

export type WsStatus = "connecting" | "open" | "closed";

type WsCtor = new (url: string) => WebSocket;

export interface WsClientOptions {
  url: string;
  onEvent: (e: WsEvent) => void;
  onStatus?: (s: WsStatus) => void;
  /** Injizierbar für Tests; default global WebSocket. */
  WebSocketImpl?: WsCtor;
  /** Basis-Backoff in ms (default 1000). */
  reconnectMs?: number;
  /** Max-Backoff in ms (default 15000). */
  maxReconnectMs?: number;
}

export interface WsClient {
  subscribe(server: string): void;
  unsubscribe(server: string): void;
  close(): void;
}

export function createWsClient(opts: WsClientOptions): WsClient {
  const Impl: WsCtor = opts.WebSocketImpl ?? (WebSocket as unknown as WsCtor);
  const baseBackoff = opts.reconnectMs ?? 1000;
  const maxBackoff = opts.maxReconnectMs ?? 15000;

  const subscribed = new Set<string>();
  let ws: WebSocket | null = null;
  let backoff = baseBackoff;
  let closedByUser = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function send(obj: unknown): void {
    if (ws && ws.readyState === 1 /* OPEN */) ws.send(JSON.stringify(obj));
  }

  function connect(): void {
    opts.onStatus?.("connecting");
    ws = new Impl(opts.url);
    ws.onopen = () => {
      backoff = baseBackoff;
      opts.onStatus?.("open");
      for (const s of subscribed) send({ action: "subscribe", server: s });
    };
    ws.onmessage = (ev: MessageEvent) => {
      let msg: WsEvent;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg && typeof msg.type === "string") opts.onEvent(msg);
    };
    ws.onclose = () => {
      opts.onStatus?.("closed");
      if (closedByUser) return;
      reconnectTimer = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, maxBackoff);
    };
    ws.onerror = () => {
      // onclose folgt; Reconnect-Logik liegt dort.
    };
  }

  function subscribe(server: string): void {
    subscribed.add(server);
    send({ action: "subscribe", server });
  }

  function unsubscribe(server: string): void {
    subscribed.delete(server);
    send({ action: "unsubscribe", server });
  }

  function close(): void {
    closedByUser = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    ws?.close();
  }

  connect();
  return { subscribe, unsubscribe, close };
}
